import React, { useState } from 'react';
import axios from 'axios';
import { ShoppingCart, CreditCard, CheckCircle, Phone, Mail, MapPin, Info, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { siteInfo, products, travelFees } from '../data/mock';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Commander = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zip: '',
    product: '',
    withInstallation: true,
    travelDistance: '0-20',
    quantity: 1,
    vehicleInfo: '',
    message: ''
  });

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const selectedProduct = products.find(p => p.code === formData.product);
  const unitPrice = selectedProduct
    ? (formData.withInstallation ? selectedProduct.priceWithInstall : selectedProduct.priceWithoutInstall)
    : 0;
  const subTotal = unitPrice * formData.quantity;

  // Frais de déplacement appliqués uniquement si pose
  const travelOption = travelFees.find(t => {
    if (formData.travelDistance === '0-20') return t.range === '0 – 20 km';
    if (formData.travelDistance === '20-50') return t.range === '20 – 50 km';
    if (formData.travelDistance === '50-100') return t.range === '50 – 100 km';
    if (formData.travelDistance === '100+') return t.range === '+100 km';
    return false;
  });
  const travelFee = formData.withInstallation && travelOption && travelOption.value !== null ? travelOption.value : 0;
  const travelOnQuote = formData.withInstallation && travelOption && travelOption.value === null;

  const totalPrice = subTotal + travelFee;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!selectedProduct) {
      toast({
        title: 'Kit requis',
        description: 'Veuillez sélectionner un kit avant d\'envoyer votre commande.',
        variant: 'destructive',
        duration: 4000,
      });
      return;
    }

    setSubmitting(true);

    // Construction des détails / récap envoyés au backend
    const prestation = formData.withInstallation ? 'Avec pose / installation' : 'Kit seul (sans pose)';
    const distLabel = formData.withInstallation && travelOption ? travelOption.range : '—';
    const travelLabel = formData.withInstallation
      ? (travelOnQuote ? 'Sur devis (+100 km)' : (travelFee === 0 ? 'Inclus (0–20 km)' : `+${travelFee} CHF (${travelOption.range})`))
      : 'N/A (sans pose)';
    const totalLabel = travelOnQuote ? `${totalPrice} CHF + déplacement sur devis` : `${totalPrice} CHF`;

    const details = [
      `Adresse : ${formData.address}, ${formData.zip} ${formData.city || ''}`.trim(),
      `Prestation : ${prestation}`,
      `Distance déplacement : ${distLabel}`,
      `Frais de déplacement : ${travelLabel}`,
      `Quantité : ${formData.quantity}`,
      formData.vehicleInfo ? `Véhicule : ${formData.vehicleInfo}` : null,
      `Prix unitaire : ${unitPrice} CHF`,
      `Sous-total : ${subTotal} CHF`,
      `TOTAL : ${totalLabel}`,
      formData.message ? `\nMessage du client :\n${formData.message}` : null,
    ].filter(Boolean).join('\n');

    const payload = {
      customer_name: formData.name,
      customer_email: formData.email,
      product_name: `${selectedProduct.code} — ${selectedProduct.name}`,
      product_type: selectedProduct.code,
      quantity: parseInt(formData.quantity, 10) || 1,
      price: totalLabel,
      details,
    };
    if (formData.phone) payload.phone = formData.phone;

    try {
      const res = await axios.post(`${API}/order`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (res.data && res.data.success) {
        toast({
          title: 'Commande envoyée !',
          description: 'Nous vous contacterons dans les 24h pour confirmer votre commande.',
          duration: 5000,
        });
        setFormData({
          name: '', email: '', phone: '', address: '', city: '', zip: '',
          product: '', withInstallation: true, travelDistance: '0-20',
          quantity: 1, vehicleInfo: '', message: ''
        });
      } else {
        throw new Error(res.data?.message || 'Erreur inconnue');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur réseau';
      toast({
        title: 'Échec de l\'envoi',
        description: `Veuillez réessayer ou nous contacter directement à contact@ecopra.ch (${detail})`,
        variant: 'destructive',
        duration: 7000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-16">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent">
              Commander votre Kit Ecopra
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Remplissez le formulaire ci-dessous pour commander votre kit. Installation professionnelle disponible partout en Suisse.
          </p>
        </div>

        {/* Process Steps */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-[#7CB342]/10 to-[#00BCD4]/10 rounded-lg p-6 text-center border-2 border-[#7CB342]">
              <div className="w-12 h-12 bg-gradient-to-r from-[#7CB342] to-[#00BCD4] rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-3">1</div>
              <h3 className="font-bold text-gray-900 mb-2">Choisissez votre kit</h3>
              <p className="text-sm text-gray-600">Sélectionnez le kit adapté à votre véhicule</p>
            </div>
            <div className="bg-gradient-to-br from-[#7CB342]/10 to-[#00BCD4]/10 rounded-lg p-6 text-center border-2 border-[#00BCD4]">
              <div className="w-12 h-12 bg-gradient-to-r from-[#7CB342] to-[#00BCD4] rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-3">2</div>
              <h3 className="font-bold text-gray-900 mb-2">Effectuez le paiement</h3>
              <p className="text-sm text-gray-600">Virement bancaire sur notre compte Raiffeisen</p>
            </div>
            <div className="bg-gradient-to-br from-[#7CB342]/10 to-[#00BCD4]/10 rounded-lg p-6 text-center border-2 border-[#7CB342]">
              <div className="w-12 h-12 bg-gradient-to-r from-[#7CB342] to-[#00BCD4] rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-3">3</div>
              <h3 className="font-bold text-gray-900 mb-2">Installation</h3>
              <p className="text-sm text-gray-600">Nous installons le kit chez vous en Suisse</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {/* Order Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <ShoppingCart className="text-[#00BCD4]" />
                  Formulaire de commande
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Info */}
                  <div>
                    <h3 className="font-semibold text-lg mb-4 text-gray-900">Vos coordonnées</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nom complet *
                        </label>
                        <Input
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Jean Dupont"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email *
                        </label>
                        <Input
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="jean.dupont@email.ch"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Téléphone *
                        </label>
                        <Input
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+41 79 123 45 67"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Code postal *
                        </label>
                        <Input
                          name="zip"
                          value={formData.zip}
                          onChange={handleChange}
                          placeholder="1950"
                          required
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Adresse complète *
                        </label>
                        <Input
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          placeholder="Rue de la Gare 15, Sion"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Product Selection */}
                  <div className="border-t pt-6">
                    <h3 className="font-semibold text-lg mb-4 text-gray-900">Choix du kit</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Sélectionnez votre kit *
                        </label>
                        <Select value={formData.product} onValueChange={(value) => setFormData({...formData, product: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Choisissez un kit..." />
                          </SelectTrigger>
                          <SelectContent>
                            {products.map((product) => (
                              <SelectItem key={product.code} value={product.code}>
                                {product.code} - {product.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Type de prestation *
                        </label>
                        <div className="grid grid-cols-2 gap-2 bg-gray-100 rounded-lg p-1">
                          <button
                            type="button"
                            data-testid="commander-without-install-btn"
                            onClick={() => setFormData({ ...formData, withInstallation: false })}
                            className={`flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition-all ${
                              !formData.withInstallation
                                ? 'bg-white text-[#00BCD4] shadow'
                                : 'text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            Kit seul (sans pose)
                          </button>
                          <button
                            type="button"
                            data-testid="commander-with-install-btn"
                            onClick={() => setFormData({ ...formData, withInstallation: true })}
                            className={`flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition-all ${
                              formData.withInstallation
                                ? 'bg-white text-[#7CB342] shadow'
                                : 'text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            Avec pose / installation
                          </button>
                        </div>
                        {selectedProduct && (
                          <div className="mt-2 text-sm text-gray-700">
                            <span className="font-medium">Tarif :</span>{' '}
                            <span className="font-bold text-[#00BCD4]">
                              {formData.withInstallation
                                ? `${selectedProduct.priceWithInstall} CHF avec pose`
                                : `${selectedProduct.priceWithoutInstall} CHF sans pose`}
                            </span>
                          </div>
                        )}
                      </div>

                      {formData.withInstallation && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Distance de déplacement *
                          </label>
                          <Select
                            value={formData.travelDistance}
                            onValueChange={(value) => setFormData({ ...formData, travelDistance: value })}
                          >
                            <SelectTrigger data-testid="commander-distance-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0-20">0 – 20 km — Inclus</SelectItem>
                              <SelectItem value="20-50">20 – 50 km — +40 CHF</SelectItem>
                              <SelectItem value="50-100">50 – 100 km — +80 CHF</SelectItem>
                              <SelectItem value="100+">+100 km — Sur devis</SelectItem>
                            </SelectContent>
                          </Select>
                          <div className="mt-2 bg-blue-50 border-l-4 border-[#00BCD4] p-3 rounded">
                            <p className="text-sm text-gray-700">
                              <strong className="text-[#00BCD4]">Frais de déplacement :</strong><br />
                              0–20 km : inclus • 20–50 km : +40 CHF • 50–100 km : +80 CHF • +100 km : sur devis
                            </p>
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Quantité
                        </label>
                        <Input
                          type="number"
                          name="quantity"
                          min="1"
                          max="10"
                          value={formData.quantity}
                          onChange={handleChange}
                          className="w-32"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Informations sur votre véhicule
                        </label>
                        <Input
                          name="vehicleInfo"
                          value={formData.vehicleInfo}
                          onChange={handleChange}
                          placeholder="Ex: Audi A4 2.0 TDI, année 2018"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Message / Questions
                        </label>
                        <Textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Informations complémentaires..."
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="commander-submit-btn"
                    className="w-full bg-gradient-to-r from-[#7CB342] to-[#00BCD4] hover:from-[#689F38] hover:to-[#0097A7] text-white font-semibold py-4 text-lg disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={20} />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2" size={20} />
                        Envoyer ma commande
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Summary & Payment Info */}
          <div className="space-y-6">
            {/* Order Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="text-[#00BCD4]" />
                  Résumé
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedProduct ? (
                  <div className="space-y-4">
                    <div>
                      <Badge className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] mb-2">
                        {selectedProduct.code}
                      </Badge>
                      <h4 className="font-semibold text-gray-900">{selectedProduct.name}</h4>
                      <p className="text-sm text-gray-600 mt-1">{selectedProduct.description}</p>
                    </div>
                    <div className="border-t pt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          Kit {formData.withInstallation ? 'avec pose' : 'sans pose'} ({formData.quantity}x)
                        </span>
                        <span className="font-medium" data-testid="commander-summary-unit-price">
                          {subTotal} CHF
                        </span>
                      </div>
                      {formData.withInstallation && (
                        <div className="flex justify-between text-sm text-[#7CB342]">
                          <span>✓ Pose / installation incluse</span>
                        </div>
                      )}
                      {formData.withInstallation && travelOption && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Déplacement ({travelOption.range})</span>
                          <span className="font-medium" data-testid="commander-summary-travel">
                            {travelOnQuote ? 'Sur devis' : (travelFee === 0 ? 'Inclus' : `+${travelFee} CHF`)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-lg font-bold pt-2 border-t">
                        <span>Total</span>
                        <span className="text-[#00BCD4]" data-testid="commander-summary-total">
                          {travelOnQuote ? `${totalPrice} CHF + déplacement sur devis` : `${totalPrice} CHF`}
                        </span>
                      </div>
                      <div className="text-[11px] text-gray-500 italic pt-1">
                        Prix net en CHF — TVA non applicable, entreprise non assujettie TVA.
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Sélectionnez un kit pour voir le résumé</p>
                )}
              </CardContent>
            </Card>

            {/* Payment Info */}
            <Card className="border-2 border-[#00BCD4]">
              <CardHeader className="bg-gradient-to-r from-[#7CB342]/10 to-[#00BCD4]/10">
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="text-[#00BCD4]" />
                  Informations de paiement
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Bénéficiaire</div>
                      <div className="font-semibold text-gray-900">{siteInfo.banking.beneficiary}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">IBAN</div>
                      <div className="font-mono text-sm font-semibold text-[#00BCD4] break-all">
                        {siteInfo.banking.iban}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Banque</div>
                      <div className="font-semibold text-gray-900">{siteInfo.banking.bank}</div>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-gray-700">
                      <strong>Important :</strong> Veuillez mentionner votre nom et numéro de commande dans la référence du virement.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Besoin d'aide ?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="text-[#00BCD4] flex-shrink-0" size={18} />
                  <div>
                    <div className="font-medium text-gray-900">{siteInfo.phone}</div>
                    <div className="text-gray-500">Du lundi au vendredi</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="text-[#00BCD4] flex-shrink-0" size={18} />
                  <a href={`mailto:${siteInfo.email}`} className="text-[#00BCD4] hover:underline">
                    {siteInfo.email}
                  </a>
                </div>
                <div className="flex items-start gap-3 text-sm">
                  <MapPin className="text-[#00BCD4] flex-shrink-0 mt-1" size={18} />
                  <div>
                    <div className="font-medium text-gray-900">{siteInfo.owner}</div>
                    <div className="text-gray-600">{siteInfo.address.mainCity}, {siteInfo.address.region}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Commander;
